import React, { useState, useMemo, useCallback } from 'react';
import {
  Text,
  Input,
  Field,
  Button,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular, DismissCircleRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import SidePanel from '../shared/SidePanel';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { formatRelativeTime } from '../../utils/relativeTime';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useDataModels } from '../../contexts/DataModelsContext';
import { dataModelsService } from '../../api/dataModelsService';
import type { ConnectionListItem } from '../../api/types';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    ...shorthands.padding('6px', '10px'),
    ...shorthands.borderRadius('6px'),
    cursor: 'pointer',
    marginTop: '-8px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowDisabled: {
    opacity: 0.5,
    cursor: 'default',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  connName: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
  },
  connDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    flex: '1 1 auto',
    minWidth: 0,
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
  error: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
    ...shorthands.padding('8px', '0'),
  },
});

interface ConnectionPickerPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConnectionPickerPanel: React.FC<ConnectionPickerPanelProps> = ({ isOpen, onClose }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { connections } = useConnections();
  const { createDataModel, setBuildJobId, updateDataModelStatusLocally } = useDataModels();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeConnections = useMemo(
    () => connections.filter((c) => c.status === 'ready' || c.status === 'connected'),
    [connections],
  );

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return activeConnections;
    const q = searchQuery.toLowerCase();
    return activeConnections.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.connector?.name ?? '').toLowerCase().includes(q),
    );
  }, [activeConnections, searchQuery]);

  const handleSelect = useCallback(
    async (conn: ConnectionListItem) => {
      if (isCreating) return;
      setIsCreating(true);
      setError(null);
      try {
        const autoName = `${conn.name} Model`;
        const model = await createDataModel(autoName, conn.id);
        // Navigate immediately so the user sees the progress page, not the list
        navigate(`/data-models/${model.id}`);
        onClose();
        // Start build in background — DiscoverDataPage will pick up the status change
        const { jobId } = await dataModelsService.build(model.id, { maxViews: 6 });
        setBuildJobId(model.id, jobId);
        updateDataModelStatusLocally(model.id, 'building');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create data model');
        setIsCreating(false);
      }
    },
    [isCreating, createDataModel, setBuildJobId, updateDataModelStatusLocally, onClose, navigate],
  );

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title="New Data Model">
      <Field>
        <Input
          size="medium"
          contentBefore={<SearchRegular />}
          contentAfter={searchQuery ? (
            <Button
              appearance="subtle"
              icon={<DismissCircleRegular />}
              size="small"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            />
          ) : undefined}
          placeholder="Search connections..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
        />
      </Field>

      {isCreating ? (
        <div className={styles.loadingCenter}>
          <Spinner size="small" label="Creating data model..." />
        </div>
      ) : (
        <>
          {error && <Text className={styles.error}>{error}</Text>}
          {filtered.length === 0 ? (
            <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, padding: '16px 0' }}>
              {activeConnections.length === 0
                ? 'No active data sources. Connect a data source first.'
                : 'No connections match your search.'}
            </Text>
          ) : (
            filtered.map((conn) => (
              <div
                key={conn.id}
                className={styles.row}
                onClick={() => handleSelect(conn)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(conn); } }}
              >
                <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                  <ConnectorIcon connectorId={conn.connectorId} name={conn.connector?.name ?? conn.name} size={20} />
                </span>
                <div className={styles.info}>
                  <Text className={styles.connName}>{conn.name}</Text>
                  <Text className={styles.connDesc}>
                    {conn.connector?.name ?? 'Source'} · {conn.datasetCount} {conn.datasetCount === 1 ? 'dataset' : 'datasets'}
                    {conn.lastSyncAt && ` · Synced ${formatRelativeTime(conn.lastSyncAt)}`}
                  </Text>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </SidePanel>
  );
};

export default ConnectionPickerPanel;